import React, { useState, useEffect } from 'react'
import {
  Container,
  Grid,
  Header,
  Segment,
  Card,
  Icon,
  Button,
  Dimmer,
  Loader,
  Message,
  List,
} from 'semantic-ui-react'
import { Link } from 'react-router-dom'
import { fetchCustomerTypes } from '../../../lib/datocms'
import './Customers.css'

const Customers = () => {
  const [customerTypes, setCustomerTypes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    loadCustomerTypes()
  }, [])

  const loadCustomerTypes = async () => {
    try {
      setLoading(true)
      const data = await fetchCustomerTypes()
      setCustomerTypes(data)
    } catch (err) {
      console.error('Error loading customer types:', err)
      setError('Failed to load customer information. Please try again later.')
    } finally {
      setLoading(false)
    }
  }

  const renderCustomerCard = (customer) => {
    return (
      <Card key={customer.id} as={Link} to={`/customers/${customer.slug}`} className="customer-card">
        <Card.Content>
          {customer.icon?.url && (
            <div className="customer-icon">
              <img src={customer.icon.url} alt={customer.icon.alt || customer.title} />
            </div>
          )}
          <Card.Header>{customer.title}</Card.Header>
          <Card.Description>{customer.shortDescription}</Card.Description>
        </Card.Content>
        <Card.Content extra>
          <Button primary fluid>
            Learn More <Icon name='arrow right' />
          </Button>
        </Card.Content>
      </Card>
    )
  }

  if (loading) {
    return (
      <Dimmer active inverted>
        <Loader size='large'>Loading Customer Information...</Loader>
      </Dimmer>
    )
  }

  if (error) {
    return (
      <Container style={{ marginTop: '2em' }}>
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      </Container>
    )
  }

  return (
    <div className="customers-page">
      {/* Hero Section */}
      <Segment className="customers-hero" vertical>
        <Container text textAlign='center'>
          <Header as='h1' inverted>
            Built for Every Type of Business
          </Header>
          <p className="hero-subtitle">
            Whether you're a wholesaler managing large inventories, a distributor coordinating 
            complex supply chains, or a consumer looking for the best deals, Trading Systems 
            has the tools and features tailored to your specific needs.
          </p>
          <Button primary size='large' as={Link} to='/signup'>
            Get Started Today
          </Button>
        </Container>
      </Segment>

      {/* Customer Types Overview */}
      <Segment className="customer-types-overview" vertical>
        <Container>
          <Header as='h2' textAlign='center' style={{ marginBottom: '2em' }}>
            Who We Serve
          </Header>
          
          <Grid columns={3} stackable>
            <Grid.Column>
              <div className="customer-type-preview">
                <Icon name='warehouse' size='huge' color='blue' />
                <Header as='h3'>Wholesalers</Header>
                <p>
                  Streamline your bulk operations with advanced inventory management, 
                  automated pricing rules, and multi-channel distribution capabilities.
                </p>
                <List bulleted>
                  <List.Item>Bulk order management</List.Item>
                  <List.Item>Automated pricing tiers</List.Item>
                  <List.Item>Inventory optimization</List.Item>
                </List>
              </div>
            </Grid.Column>
            <Grid.Column>
              <div className="customer-type-preview">
                <Icon name='truck' size='huge' color='green' />
                <Header as='h3'>Distributors</Header>
                <p>
                  Optimize your distribution network with real-time logistics tracking, 
                  route optimization, and seamless communication with suppliers and buyers.
                </p>
                <List bulleted>
                  <List.Item>Route optimization</List.Item>
                  <List.Item>Real-time tracking</List.Item>
                  <List.Item>Multi-location management</List.Item>
                </List>
              </div>
            </Grid.Column>
            <Grid.Column>
              <div className="customer-type-preview">
                <Icon name='shopping cart' size='huge' color='orange' />
                <Header as='h3'>Consumers</Header>
                <p>
                  Access competitive pricing, transparent transactions, and reliable 
                  delivery for all your material and product needs.
                </p>
                <List bulleted>
                  <List.Item>Competitive pricing</List.Item>
                  <List.Item>Order tracking</List.Item>
                  <List.Item>Quality assurance</List.Item>
                </List>
              </div>
            </Grid.Column>
          </Grid>
        </Container>
      </Segment>

      {/* Dynamic Customer Types from Dato CMS */}
      {customerTypes.length > 0 && (
        <Segment className="customer-types-grid" vertical>
          <Container>
            <Header as='h2' textAlign='center' style={{ marginBottom: '2em' }}>
              Explore Our Customer Solutions
            </Header>
            <Card.Group itemsPerRow={3} stackable>
              {customerTypes.map(customer => renderCustomerCard(customer))}
            </Card.Group>
          </Container>
        </Segment>
      )}

      {/* Success Stories Section */}
      <Segment className="success-stories" vertical>
        <Container>
          <Header as='h2' textAlign='center' style={{ marginBottom: '2em' }}>
            Success Across Industries
          </Header>
          
          <Grid columns={2} stackable>
            <Grid.Column>
              <Segment>
                <Header as='h3'>
                  <Icon name='quote left' />
                  Transformed Our Operations
                </Header>
                <p>
                  "Trading Systems has completely transformed how we manage our wholesale 
                  lumber business. The platform's inventory management and automated pricing 
                  have saved us countless hours and increased our margins by 15%."
                </p>
                <p>
                  <strong>- John Smith, ABC Lumber Co.</strong>
                </p>
              </Segment>
            </Grid.Column>
            <Grid.Column>
              <Segment>
                <Header as='h3'>
                  <Icon name='quote left' />
                  Streamlined Distribution
                </Header>
                <p>
                  "As a distributor, coordination was our biggest challenge. Trading Systems' 
                  real-time tracking and communication tools have made our operations 50% 
                  more efficient."
                </p>
                <p>
                  <strong>- Sarah Johnson, Regional Distributors Inc.</strong>
                </p>
              </Segment>
            </Grid.Column>
          </Grid>
        </Container>
      </Segment>

      {/* Key Benefits Section */}
      <Segment className="key-benefits" vertical>
        <Container>
          <Header as='h2' textAlign='center' style={{ marginBottom: '2em' }}>
            Why Businesses Choose Trading Systems
          </Header>
          
          <Grid columns={4} stackable>
            <Grid.Column>
              <div className="benefit-item">
                <Icon name='chart line' size='large' color='blue' />
                <Header as='h4'>Increased Efficiency</Header>
                <p>Automate repetitive tasks and streamline operations</p>
              </div>
            </Grid.Column>
            <Grid.Column>
              <div className="benefit-item">
                <Icon name='dollar' size='large' color='green' />
                <Header as='h4'>Better Margins</Header>
                <p>Optimize pricing and reduce operational costs</p>
              </div>
            </Grid.Column>
            <Grid.Column>
              <div className="benefit-item">
                <Icon name='users' size='large' color='orange' />
                <Header as='h4'>Stronger Relationships</Header>
                <p>Build lasting partnerships with suppliers and buyers</p>
              </div>
            </Grid.Column>
            <Grid.Column>
              <div className="benefit-item">
                <Icon name='lock' size='large' color='red' />
                <Header as='h4'>Secure Transactions</Header>
                <p>Enterprise-grade security for all your business data</p>
              </div>
            </Grid.Column>
          </Grid>
        </Container>
      </Segment>

      {/* Integration Partners */}
      <Segment className="integration-partners" vertical>
        <Container>
          <Header as='h2' textAlign='center' style={{ marginBottom: '2em' }}>
            Seamless Integration with Your Existing Tools
          </Header>
          <p textAlign='center' style={{ marginBottom: '3em' }}>
            Trading Systems integrates with the tools you already use, making adoption 
            smooth and efficient for your team.
          </p>
          
          <Grid columns={6} stackable textAlign='center'>
            <Grid.Column>
              <Segment placeholder>
                <Header icon>
                  <Icon name='file excel' />
                  QuickBooks
                </Header>
              </Segment>
            </Grid.Column>
            <Grid.Column>
              <Segment placeholder>
                <Header icon>
                  <Icon name='ship' />
                  Logistics Pro
                </Header>
              </Segment>
            </Grid.Column>
            <Grid.Column>
              <Segment placeholder>
                <Header icon>
                  <Icon name='database' />
                  SAP
                </Header>
              </Segment>
            </Grid.Column>
            <Grid.Column>
              <Segment placeholder>
                <Header icon>
                  <Icon name='cloud' />
                  Salesforce
                </Header>
              </Segment>
            </Grid.Column>
            <Grid.Column>
              <Segment placeholder>
                <Header icon>
                  <Icon name='credit card' />
                  Stripe
                </Header>
              </Segment>
            </Grid.Column>
            <Grid.Column>
              <Segment placeholder>
                <Header icon>
                  <Icon name='mail' />
                  Mailchimp
                </Header>
              </Segment>
            </Grid.Column>
          </Grid>
        </Container>
      </Segment>

      {/* CTA Section */}
      <Segment className="customers-cta" inverted vertical>
        <Container textAlign='center'>
          <Header as='h2' inverted>
            Join Thousands of Successful Businesses
          </Header>
          <p style={{ fontSize: '1.1em', marginBottom: '2em' }}>
            Start your journey with Trading Systems today and experience the difference 
            our platform can make for your business.
          </p>
          <Button size='large' inverted as={Link} to='/signup'>
            Start Free Trial
          </Button>
          <Button size='large' inverted basic as={Link} to='/contact-us'>
            Schedule a Demo
          </Button>
        </Container>
      </Segment>
    </div>
  )
}

export default Customers